"use client";

import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import * as React from "react";
import { type ReactNode } from "react";
import { Dialog, Label, PasswordField, Button } from "./elements.js";
import { Form } from "radix-ui";
import { useCreatePassword } from "../api/endpoint.js";
import { useSecuritySettings } from "./use-security-settings.js";
import { ElevatedAccess } from "./elevated-access.js";
import { SaveButton } from "./save-button.js";
import { useDialogClose } from "./use-dialog-close.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

interface SetPasswordDialogProps extends Dialog.RootProps {
  children?: ReactNode;
}

export function SetPasswordDialog({
  children,
  ...props
}: SetPasswordDialogProps) {
  const [open, setOpen] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Dialog.Root {...props} open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>{children}</Dialog.Trigger>

      <Dialog.Content maxWidth="480px">
        <ElevatedAccess>
          <Content onClose={handleClose} />
        </ElevatedAccess>
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface ContentProps {
  onClose: () => void;
}

function Content({ onClose }: ContentProps) {
  return <SetPasswordForm onSuccess={onClose} />;
}

interface SetPasswordFormProps {
  onSuccess?: () => void;
  cancel?: React.ReactNode;
  description?: React.ReactNode;
}

export function SetPasswordForm({
  onSuccess,
  cancel,
  description,
}: SetPasswordFormProps) {
  const translate = useTranslation();
  const setPassword = useCreatePassword();
  const securitySettings = useSecuritySettings();
  const [disableSubmit, setDisableSubmit] = React.useState(true);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const password = formData.get("password")?.toString();

    if (!password) {
      return;
    }

    setPassword.mutate({ data: { password } });
  };

  useDialogClose(setPassword.isSuccess, () => {
    securitySettings.update("Password", true);
  });

  return (
    <Flex direction="column" gap="5">
      <Flex direction="column" gap="2">
        <Dialog.Title>
          <Translation
            defaultMessage="Set a password"
            id="0N6IHb"
            description="Dialog title for setting a password"
          />
        </Dialog.Title>
        {description ?? (
          <VisuallyHidden>
            <Dialog.Description>
              <Translation
                defaultMessage="Set a new password for your account"
                id="KYv5vS"
                description="Screen reader description for set password dialog"
              />
            </Dialog.Description>
          </VisuallyHidden>
        )}
      </Flex>

      {setPassword.error ? (
        <Callout.Root color="red">
          <Callout.Text>
            {getMutationErrorMessage(setPassword.error)}
          </Callout.Text>
        </Callout.Root>
      ) : null}

      <Form.Root
        onSubmit={handleSubmit}
        onChange={(event) => {
          const formData = new FormData(event.currentTarget);
          const password = formData.get("password")?.toString();
          const confirmPassword = formData.get("confirmPassword")?.toString();
          setDisableSubmit(password === "" || confirmPassword === "");
        }}
      >
        <Flex direction="column" gap="5">
          <Flex direction="column" gap="4">
            <Form.Field name="password" asChild>
              <Flex direction="column" gap="2">
                <Form.Label asChild>
                  <Label>
                    <Translation
                      defaultMessage="Enter a password"
                      id="YxbwGs"
                      description="Label for password input field"
                    />
                  </Label>
                </Form.Label>
                <Form.Control asChild>
                  <PasswordField
                    autoFocus
                    required
                    autoComplete="new-password"
                    placeholder={translate({
                      defaultMessage: "Password",
                      id: "HYO1Ra",
                      description: "Placeholder for password input",
                    })}
                    disabled={setPassword.isPending || setPassword.isSuccess}
                  />
                </Form.Control>
                <Form.Message match="valueMissing" asChild>
                  <Text size="2" color="red">
                    <Translation
                      defaultMessage="Please enter a new password"
                      id="+2kQs5"
                      description="Error message when password field is empty"
                    />
                  </Text>
                </Form.Message>
                <Form.Message match={(value) => value.length < 8} asChild>
                  <Text size="2" color="red">
                    <Translation
                      defaultMessage="Password must be at least 8 characters"
                      id="WRjyrh"
                      description="Error message when password is too short"
                    />
                  </Text>
                </Form.Message>
              </Flex>
            </Form.Field>

            <Form.Field name="confirmPassword" asChild>
              <Flex direction="column" gap="2">
                <Form.Label asChild>
                  <Label>
                    <Translation
                      defaultMessage="Confirm your password"
                      id="/c8Kki"
                      description="Label for password confirmation field"
                    />
                  </Label>
                </Form.Label>
                <Form.Control asChild>
                  <PasswordField
                    required
                    autoComplete="new-password"
                    placeholder={translate({
                      defaultMessage: "Confirm password",
                      id: "1VNN1w",
                      description:
                        "Placeholder for password confirmation input",
                    })}
                    disabled={setPassword.isPending || setPassword.isSuccess}
                  />
                </Form.Control>
                <Form.Message match="valueMissing" asChild>
                  <Text size="2" color="red">
                    <Translation
                      defaultMessage="Please confirm your new password"
                      id="vfwqWf"
                      description="Error message when password confirmation is empty"
                    />
                  </Text>
                </Form.Message>
                <Form.Message
                  match={(value, formData) =>
                    value !== formData.get("password")
                  }
                  asChild
                >
                  <Text size="2" color="red">
                    <Translation
                      defaultMessage="The passwords you entered don't match."
                      id="ayYwQx"
                      description="Error message when passwords don't match"
                    />
                  </Text>
                </Form.Message>
              </Flex>
            </Form.Field>
          </Flex>

          <Flex gap="3" justify="end">
            {cancel ?? (
              <Dialog.Close>
                <Button
                  variant="secondary"
                  disabled={setPassword.isPending || setPassword.isSuccess}
                >
                  <Translation
                    defaultMessage="Cancel"
                    id="VJVs4m"
                    description="Button to cancel setting password"
                  />
                </Button>
              </Dialog.Close>
            )}

            <SaveButton
              loading={setPassword.isPending}
              done={setPassword.isSuccess}
              onDone={onSuccess}
              type="submit"
              disabled={disableSubmit || undefined}
            >
              <Translation
                defaultMessage="Set password"
                id="jSfqX/"
                description="Button to confirm setting new password"
              />
            </SaveButton>
          </Flex>
        </Flex>

        {/* mirror errors in a live region */}
        <VisuallyHidden asChild>
          <section aria-live="polite">
            {getMutationErrorMessage(setPassword.error)}
          </section>
        </VisuallyHidden>
      </Form.Root>
    </Flex>
  );
}

function getMutationErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return (
    <Translation
      defaultMessage="Something went wrong. Please try again."
      id="jLzoYu"
      description="Generic error message for password setting failure"
    />
  );
}
